import React from 'react';
import {StyledProps, mediaQueries, ThemedProps} from '../modules/common';
import withTranslation from 'next-translate/withTranslation';
import {I18n} from '../modules/util/i18n/i18n-interface';
import {withAnalytics} from '../modules/analytics/analytics';
import {WithAnalytics} from '../lib/analytics/analytics';
import {ChangeLanguageLink} from './change-language-link';
// eslint-disable-next-line import/named,
import {styled, withTheme, DefaultTheme} from 'styled-components';
import {typographyBodyStyles} from '../modules/common/styles';
import {getLanguageAwareURL} from '../modules/util/utilities/get-language-aware-url';

export interface FooterProps extends StyledProps, WithAnalytics {
    theme?: DefaultTheme;
    i18n: I18n;
    currentPath: string;
    teloaFooter: boolean;
}

const UnstyledFooter = (props: FooterProps): JSX.Element => {
    const {className, teloaFooter, currentPath} = props;
    const {t} = props.i18n;

    const PROJECT_NAME = process.env.NEXT_PUBLIC_PROJECT_NAME_DISPLAY;
    const START_YEAR =
        (process.env.NEXT_PUBLIC_PROJECT_NAME as string) === 'fonoma'
            ? '2014'
            : '2023';
    const address = teloaFooter
        ? t('footer:teloa-address')
        : t('footer:address');

    return (
        <div className={className}>
            <Wrapper>
                <div>{address}</div>
                <Separator>·</Separator>
                <div>
                    {t('footer:copyright', {
                        project: PROJECT_NAME,
                        startYear: START_YEAR,
                        year: new Date().getFullYear(),
                    })}
                </div>
            </Wrapper>
            <LinkWrapper>
                <div>
                    <Link
                        href={getLanguageAwareURL('privacy', props.i18n.lang)}
                    >
                        {t('footer:privacy-policy')}
                    </Link>
                    <CenterLink
                        href={getLanguageAwareURL('terms', props.i18n.lang)}
                    >
                        {t('footer:terms-of-use')}
                    </CenterLink>
                </div>
                <StyledChangeLanguageLink currentPath={currentPath} />
            </LinkWrapper>
        </div>
    );
};

const StyledChangeLanguageLink = styled(ChangeLanguageLink)`
    ${typographyBodyStyles}
    color: ${({theme}: ThemedProps): string => theme.colors.plancton};
`;

const Separator = styled.div`
    margin: 0 8px;
    ${mediaQueries.lessThan('small')`
        display:none;
    `}
`;

const Wrapper = styled.section`
    display: flex;
    justify-content: center;
    line-height: 24px;
    margin-bottom: 8px;

    max-width: ${(props): string =>
        `${props.theme.desktopMeasures.centeredContentWidth}${props.theme.unitsOfMeasure.px}`};

    ${mediaQueries.lessThan('small')`     
        flex-direction: column;
        justify-content: flex-start;
        margin-bottom: 0;
    `}
`;

const LinkWrapper = styled.div`
    display: flex;

    ${mediaQueries.lessThan('small')`
        flex-direction: column;
    `}
`;

const Link = styled.a`
    text-decoration: none;
    color: ${({theme}: ThemedProps): string => theme.colors.plancton};

    :hover {
        cursor: pointer;
    }
`;

const CenterLink = styled(Link)`
    margin-left: 24px;
    margin-right: 24px;

    ${mediaQueries.lessThan('small')`
        margin-left: 16px;
        margin-right: 0;
    `}
`;

const BasicFooter = styled(UnstyledFooter)`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    letter-spacing: normal;
    border-top: 1px solid ${(props): string => props.theme.colors.caballa};
    background-color: ${(props): string => props.theme.colors.arroz};
    min-height: ${(props): string =>
        `${props.theme.desktopMeasures.footerHeight}${props.theme.unitsOfMeasure.px}`};
    padding: 24px 16px;

    div,
    a,
    button {
        align-self: center;
        line-height: 24px;
    }

    div {
        color: ${(props): string => props.theme.colors.ballena};
    }

    ${mediaQueries.lessThan('small')`
        align-items: start;
        padding-bottom: 0;
        min-height: ${(props): string =>
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-unsafe-member-access
            `${props.theme.mobileMeasures.footerHeight}${props.theme.unitsOfMeasure.px}`};
                
        div, a, button{
            align-self: flex-start;            
        }
        
        div{        
            padding-bottom: 16px;
        }
    `}
`;

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
const Footer = withAnalytics(withTranslation(withTheme(BasicFooter)));

export {Footer};
